import { useMemo, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Layers, ChevronRight, Search, X, Loader2 } from "lucide-react";

const diffColor = {
  初级: "text-emerald-500",
  中级: "text-amber-500",
  高级: "text-rose-500",
};

// Highlight every case-insensitive occurrence of `query` inside `text`.
const Highlight = ({ text, query }) => {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  const parts = [];
  let rest = text;
  let lower = text.toLowerCase();
  const q = query.toLowerCase();
  let key = 0;
  let cursor = 0;
  let pos = lower.indexOf(q, cursor);
  while (pos !== -1) {
    if (pos > cursor) parts.push(rest.slice(cursor, pos));
    parts.push(
      <mark key={key++} className="search-hl">
        {rest.slice(pos, pos + q.length)}
      </mark>
    );
    cursor = pos + q.length;
    pos = lower.indexOf(q, cursor);
  }
  if (cursor < rest.length) parts.push(rest.slice(cursor));
  return parts;
};

// Shimmer placeholder rows shown while the course list is loading.
const CourseListSkeleton = () => (
  <div className="py-2" data-testid="course-list-skeleton" aria-busy="true">
    {[0, 1, 2].map((g) => (
      <div key={g} className="mb-1">
        <div className="px-4 pt-3 pb-1">
          <div className="skel h-2.5 w-20 rounded" style={{ animationDelay: `${g * 90}ms` }} />
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="flex items-start gap-2 px-4 py-2 border-l-2 border-transparent"
          >
            <div className="skel h-3.5 w-3.5 rounded-full mt-0.5 shrink-0" style={{ animationDelay: `${(g * 4 + i) * 60}ms` }} />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="skel h-3 rounded" style={{ width: `${60 + ((i * 13) % 35)}%`, animationDelay: `${(g * 4 + i) * 60}ms` }} />
              <div className="skel h-2 w-8 rounded" style={{ animationDelay: `${(g * 4 + i) * 60 + 40}ms` }} />
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

export const Sidebar = ({ courses, activeId, onSelect, loading = false }) => {  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return courses;
    return courses.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q) ||
        (c.category || "").toLowerCase().includes(q)
    );
  }, [courses, query]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((c) => {
      if (!map.has(c.category)) map.set(c.category, []);
      map.get(c.category).push(c);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <aside className="h-full flex flex-col bg-[hsl(var(--surface))]">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 shrink-0">
        <Layers className="h-4 w-4 text-blue-500" />
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
          课程目录
        </span>
        <span className="ml-auto text-[10px] font-mono text-muted-foreground">
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin text-blue-500" data-testid="course-list-spinner" />
          ) : query ? (
            `${filtered.length}/${courses.length}`
          ) : (
            courses.length
          )}
        </span>
      </div>

      {/* Search */}
      <div className="px-3 py-2.5 border-b border-border shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            data-testid="course-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading}
            placeholder="搜索课程…"
            className="w-full h-8 rounded-md bg-muted/60 border border-border pl-8 pr-7 text-xs font-mono text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-blue-500/60 transition-shadow disabled:opacity-50"
          />
          {query && (
            <button
              data-testid="course-search-clear"
              onClick={() => setQuery("")}
              aria-label="清空搜索"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <CourseListSkeleton />
        ) : filtered.length === 0 ? (
          <div
            data-testid="search-empty"
            className="px-4 py-10 text-center text-xs font-mono text-muted-foreground"
          >
            没有匹配 “{query}” 的课程
          </div>
        ) : (
          <nav className="py-2">
            {grouped.map(([cat, items]) => (
              <div key={cat} className="mb-1">
                <div className="px-4 pt-3 pb-1 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/70">
                  <Highlight text={cat} query={query} />
                </div>
                {items.map((c) => {
                  const active = c.id === activeId;
                  return (
                    <button
                      key={c.id}
                      data-testid={`course-item-${c.id}`}
                      onClick={() => onSelect(c.id)}
                      className={`w-full text-left flex items-start gap-2 px-4 py-2 border-l-2 transition-colors ${
                        active
                          ? "border-foreground bg-muted/60"
                          : "border-transparent hover:bg-muted/40"
                      }`}
                    >
                      <ChevronRight
                        className={`h-3.5 w-3.5 mt-0.5 shrink-0 transition-transform ${
                          active ? "text-foreground translate-x-0.5" : "text-muted-foreground"
                        }`}
                      />
                      <span className="min-w-0">
                        <span
                          className={`block text-sm leading-tight truncate ${
                            active ? "text-foreground font-medium" : "text-foreground/80"
                          }`}
                        >
                          <Highlight text={c.title} query={query} />
                        </span>
                        <span
                          className={`text-[10px] font-mono ${diffColor[c.difficulty] || "text-muted-foreground"}`}
                        >
                          {c.difficulty}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        )}
      </ScrollArea>
    </aside>
  );
};
